import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { wishlistService } from '../../services/api';

// Async thunks
export const getWishlist = createAsyncThunk(
    'wishlist/getWishlist',
    async (_, { rejectWithValue }) => {
        try {
            const response = await wishlistService.getWishlist();
            if (response.success) {
                return response.wishlist || [];
            }
            return rejectWithValue(response.message || 'Failed to fetch wishlist');
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const addToWishlist = createAsyncThunk(
    'wishlist/addToWishlist',
    async (product, { rejectWithValue }) => {
        try {
            const response = await wishlistService.addToWishlist(product);
            if (response.success) {
                // Return the full product object
                return product;
            }
            return rejectWithValue(response.message || 'Failed to add to wishlist');
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const removeFromWishlist = createAsyncThunk(
    'wishlist/removeFromWishlist',
    async (productId, { rejectWithValue }) => {
        try {
            const response = await wishlistService.removeFromWishlist(productId);
            if (response.success) {
                return productId;
            }
            return rejectWithValue(response.message || 'Failed to remove from wishlist');
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

const normalizeWishlistProduct = (item) => {
    if (!item) return null;
    const prod = item.product || item;
    const id = prod.id || item.product_id || item.id;
    
    const productData = {
        ...prod,
        id: id,
        name: prod.name || item.name || item.product_name || 'Product',
        slug: prod.slug || item.slug || '',
        description: prod.description || item.description || '',
        price: prod.price ?? item.price ?? item.product_price ?? 0,
        original_price: prod.original_price ?? item.original_price ?? null,
        discount: prod.discount ?? item.discount ?? null,
        brand: prod.brand || item.brand || '',
        product_flag: prod.product_flag || item.product_flag || '',
        sku: prod.sku || item.sku || '',
        stock_quantity: prod.stock_quantity ?? item.stock_quantity ?? 0,
        main_image: prod.main_image || prod.image || item.main_image || item.image || prod.images?.[0] || item.images?.[0] || 'https://via.placeholder.com/300x400?text=No+Image',
        image: prod.main_image || prod.image || item.main_image || item.image || prod.images?.[0] || item.images?.[0] || 'https://via.placeholder.com/300x400?text=No+Image',
        images: prod.images || item.images || [],
        colors: prod.colors || item.colors || [],
        sizes: prod.sizes || item.sizes || [],
        rating: prod.rating ?? item.rating ?? 4,
        reviews_count: prod.reviews_count ?? item.reviews_count ?? 0,
        is_new: prod.is_new ?? item.is_new ?? false,
        is_trending: prod.is_trending ?? item.is_trending ?? false,
        is_featured: prod.is_featured ?? item.is_featured ?? false,
    };
    
    delete productData.product;
    delete productData.wishlist_id;
    delete productData.added_at;

    return {
        ...productData,
        wishlist_id: item.wishlist_id || item.id || id,
        added_at: item.added_at || item.created_at || new Date().toISOString(),
        product: productData
    };
};

const initialState = {
    wishlistItems: [],
    loading: false,
    error: null,
    totalItems: 0
};

const wishlistSlice = createSlice({
    name: 'wishlist',
    initialState,
    reducers: {
        clearWishlist: (state) => {
            state.wishlistItems = [];
            state.totalItems = 0;
        },
        toggleWishlistLocal: (state, action) => {
            const normProduct = normalizeWishlistProduct(action.payload);
            if (!normProduct) return;
            const existsIndex = state.wishlistItems.findIndex(item => (item.id || item.product?.id) === normProduct.id);
            if (existsIndex !== -1) {
                state.wishlistItems.splice(existsIndex, 1);
                state.totalItems = state.wishlistItems.length;
            } else {
                state.wishlistItems.push(normProduct);
                state.totalItems = state.wishlistItems.length;
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // Get Wishlist
            .addCase(getWishlist.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getWishlist.fulfilled, (state, action) => {
                state.loading = false;
                state.wishlistItems = (action.payload || []).map(normalizeWishlistProduct).filter(Boolean);
                state.totalItems = state.wishlistItems.length;
            })
            .addCase(getWishlist.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.wishlistItems = [];
                state.totalItems = 0;
            })
            // Add to Wishlist
            .addCase(addToWishlist.pending, (state) => {
                state.error = null;
            })
            .addCase(addToWishlist.fulfilled, (state, action) => {
                const normProduct = normalizeWishlistProduct(action.payload);
                if (normProduct) {
                    const exists = state.wishlistItems.some(item => (item.id || item.product?.id) === normProduct.id);
                    if (!exists) {
                        state.wishlistItems.push(normProduct);
                        state.totalItems = state.wishlistItems.length;
                    }
                }
            })
            .addCase(addToWishlist.rejected, (state, action) => {
                state.error = action.payload;
            })
            // Remove from Wishlist
            .addCase(removeFromWishlist.fulfilled, (state, action) => {
                const productId = action.payload;
                state.wishlistItems = state.wishlistItems.filter(item => (item.id || item.product?.id) !== productId);
                state.totalItems = state.wishlistItems.length;
            });
    }
});

export const { clearWishlist, toggleWishlistLocal } = wishlistSlice.actions;
export default wishlistSlice.reducer;